/**
 * Accounting page — Holded-style minimal. Visible to admin/coordinator only.
 * Tabs: Resumen | Facturas | Gastos
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Trash2, FileText, Receipt, TrendingUp, TrendingDown, Wallet, Lock, PiggyBank, Briefcase } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import StateBadge from "@/components/StateBadge";
import KPICard from "@/components/KPICard";
import { useAuth } from "@/contexts/AuthContext";

const INVOICE_STATE_LABELS = {
    draft: { label: "Borrador", color: "bg-zentra-bg text-zentra-ink border-zentra-border" },
    sent: { label: "Enviada", color: "bg-sky-100 text-sky-900 border-sky-200" },
    paid: { label: "Pagada", color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
    overdue: { label: "Vencida", color: "bg-red-100 text-red-800 border-red-200" },
    cancelled: { label: "Cancelada", color: "bg-zinc-200 text-zinc-700 border-zinc-300" },
};

const EXPENSE_CATEGORY_LABELS = {
    payroll: "Nóminas",
    supplies: "Suministros / productos",
    utilities: "Luz / agua / gas",
    rent: "Alquileres",
    marketing: "Marketing",
    fees: "Comisiones",
    transport: "Transporte / combustible",
    maintenance: "Mantenimiento",
    other: "Otros",
};

const eur = (n) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

export default function Accounting() {
    const { user } = useAuth();
    const [tab, setTab] = useState("summary");
    const [summary, setSummary] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [invoiceModal, setInvoiceModal] = useState(false);
    const [expenseModal, setExpenseModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, i, e, c] = await Promise.all([
                api.get("/accounting/summary"),
                api.get("/invoices"),
                api.get("/expenses"),
                api.get("/clients"),
            ]);
            setSummary(s.data);
            setInvoices(i.data || []);
            setExpenses(e.data || []);
            setClients(c.data || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

    if (!user || !["super_admin", "admin", "coordinator"].includes(user.role)) {
        return (
            <div className="zentra-card-static p-10 text-center" data-testid="accounting-no-access">
                <Lock className="mx-auto text-zentra-muted mb-3" />
                <p className="font-display font-semibold text-zentra-ink">Acceso restringido</p>
                <p className="text-sm text-zentra-muted mt-1">
                    Solo administradores y coordinadores pueden ver la contabilidad.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="accounting-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Control económico · solo admin/coord
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Contabilidad
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Facturación a clientes, gastos operativos y márgenes del mes — al estilo Holded.
                    </p>
                </div>
                {tab === "invoices" && (
                    <button className="zentra-btn-primary" onClick={() => setInvoiceModal(true)} data-testid="new-invoice-button">
                        <Plus size={16} /> Nueva factura
                    </button>
                )}
                {tab === "expenses" && (
                    <button className="zentra-btn-primary" onClick={() => setExpenseModal(true)} data-testid="new-expense-button">
                        <Plus size={16} /> Nuevo gasto
                    </button>
                )}
            </div>

            {/* Tab nav */}
            <div className="flex items-center gap-1 border-b border-zentra-border" data-testid="accounting-tabs">
                {[
                    { id: "summary", label: "Resumen", icon: TrendingUp },
                    { id: "clients", label: "Clientes", icon: Briefcase },
                    { id: "invoices", label: "Facturas", icon: FileText },
                    { id: "expenses", label: "Gastos", icon: Receipt },
                ].map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${
                            tab === t.id
                                ? "border-zentra-accent-strong text-zentra-ink"
                                : "border-transparent text-zentra-muted hover:text-zentra-ink"
                        }`}
                        data-testid={`tab-${t.id}`}
                    >
                        <t.icon size={14} className="inline mr-1.5 -mt-0.5" />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Body */}
            {loading ? (
                <div className="zentra-skeleton h-80" />
            ) : tab === "summary" ? (
                <SummaryView summary={summary} />
            ) : tab === "invoices" ? (
                <InvoicesView
                    invoices={invoices}
                    clientMap={clientMap}
                    onUpdate={async (id, patch) => {
                        await api.patch(`/invoices/${id}`, patch);
                        load();
                    }}
                    onDelete={async (id) => {
                        if (!window.confirm("¿Eliminar factura?")) return;
                        await api.delete(`/invoices/${id}`);
                        load();
                    }}
                />
            ) : (
                <ExpensesView
                    expenses={expenses}
                    onDelete={async (id) => {
                        if (!window.confirm("¿Eliminar gasto?")) return;
                        await api.delete(`/expenses/${id}`);
                        load();
                    }}
                />
            )}

            <InvoiceModal open={invoiceModal} onClose={() => setInvoiceModal(false)} clients={clients} onSaved={load} />
            <ExpenseModal open={expenseModal} onClose={() => setExpenseModal(false)} onSaved={load} />
        </div>
    );
}

function SummaryView({ summary }) {
    if (!summary) return null;
    const maxMonth = Math.max(1, ...summary.monthly.map((m) => Math.max(m.revenue, m.expenses)));
    const taxes = summary.taxes || {};
    const personnel = summary.personnel || {};
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard
                    title="Ingresos cobrados"
                    value={eur(summary.total_revenue_paid)}
                    icon={Wallet}
                    accent
                    hint={`${summary.invoices_count} facturas`}
                    testId="kpi-revenue"
                />
                <KPICard
                    title="Pendiente cobro"
                    value={eur(summary.pending)}
                    icon={FileText}
                    hint="Enviadas/vencidas"
                    testId="kpi-pending"
                />
                <KPICard
                    title="Gastos + personal"
                    value={eur(summary.total_expenses_with_payroll)}
                    icon={TrendingDown}
                    hint={`${summary.expenses_count} apuntes · ${personnel.headcount || 0} empleados`}
                    testId="kpi-expenses"
                />
                <KPICard
                    title="Beneficio neto"
                    value={eur(summary.profit)}
                    icon={PiggyBank}
                    accent={summary.profit >= 0}
                    hint={`Margen ${summary.margin_pct}%`}
                    testId="kpi-profit"
                />
            </div>

            {/* Tax panel — IVA y SS empresa */}
            <div className="zentra-card-static overflow-hidden" data-testid="tax-panel">
                <div className="bg-zentra-primary text-white px-5 py-3 flex items-center justify-between">
                    <div>
                        <p className="text-[11px] font-semibold tracking-widest uppercase opacity-80">Impuestos del periodo</p>
                        <h3 className="font-display font-semibold text-lg">A pagar a Hacienda y Seguridad Social</h3>
                    </div>
                    <div className="text-right">
                        <p className="text-[11px] uppercase tracking-widest opacity-80">Total a pagar</p>
                        <p className="font-display text-2xl font-bold" data-testid="taxes-total">
                            {eur(taxes.total_a_pagar)}
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zentra-border">
                    <TaxBlock
                        label="IVA repercutido"
                        value={eur(taxes.iva_repercutido)}
                        sub={`Sobre base ${eur(taxes.base_imponible_ingresos)}`}
                        testId="iva-repercutido"
                    />
                    <TaxBlock
                        label="IVA soportado (gastos)"
                        value={`− ${eur(taxes.iva_soportado)}`}
                        sub="Deducible en modelo 303"
                        testId="iva-soportado"
                    />
                    <TaxBlock
                        label="IVA a pagar"
                        value={eur(taxes.iva_a_pagar)}
                        sub="Repercutido − Soportado"
                        highlight={taxes.iva_a_pagar > 0 ? "warn" : "ok"}
                        testId="iva-a-pagar"
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zentra-border border-t border-zentra-border">
                    <TaxBlock
                        label="IRPF retenido por clientes"
                        value={eur(taxes.irpf_retenido)}
                        sub="Informativo · ya descontado en facturas"
                        testId="irpf-retenido"
                    />
                    <TaxBlock
                        label="Seguridad Social a cargo empresa"
                        value={eur(taxes.employer_social_security)}
                        sub={`${personnel.employer_social_security_rate_pct || 31}% sobre ${eur(personnel.gross_total)} de nóminas`}
                        highlight="warn"
                        testId="ss-empresa"
                    />
                </div>
            </div>

            {/* Personnel cost panel */}
            <div className="zentra-card-static p-5" data-testid="personnel-cost-panel">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <h3 className="font-display font-semibold text-zentra-ink">Coste de personal</h3>
                        <p className="text-xs text-zentra-muted">
                            Calculado automáticamente desde los salarios brutos del personal activo. Edita la nómina bruta en Personal.
                        </p>
                    </div>
                    <span className="zentra-chip bg-zentra-accent/20 border-zentra-accent text-zentra-primary">
                        {personnel.headcount || 0} empleados activos
                    </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <Statlet label="Bruto mensual" value={eur(personnel.monthly_gross)} hint="Suma de salarios brutos" />
                    <Statlet label={`Bruto del periodo (${summary.period?.months || 1} mes${(summary.period?.months || 1) > 1 ? "es" : ""})`} value={eur(personnel.gross_total)} />
                    <Statlet label={`SS empresa (${personnel.employer_social_security_rate_pct || 31}%)`} value={eur(personnel.employer_social_security)} hint="Cuota patronal" />
                    <Statlet label="Coste total" value={eur(personnel.total)} highlight />
                </div>
            </div>

            {/* Monthly chart (simple bars) */}
            <div className="zentra-card-static p-5">
                <h3 className="font-display font-semibold text-zentra-ink mb-4">Evolución mensual</h3>
                <div className="space-y-3">
                    {summary.monthly.map((m) => (
                        <div key={m.month} className="grid grid-cols-[80px_1fr_1fr] gap-3 items-center text-xs">
                            <span className="font-mono-zentra text-zentra-muted">{m.month}</span>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 h-3 bg-zentra-bg rounded-sm overflow-hidden border border-zentra-border">
                                    <div
                                        className="h-full bg-emerald-500"
                                        style={{ width: `${(m.revenue / maxMonth) * 100}%` }}
                                    />
                                </div>
                                <span className="text-emerald-700 font-semibold w-20 text-right">{eur(m.revenue)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 h-3 bg-zentra-bg rounded-sm overflow-hidden border border-zentra-border">
                                    <div
                                        className="h-full bg-red-400"
                                        style={{ width: `${(m.expenses / maxMonth) * 100}%` }}
                                    />
                                </div>
                                <span className="text-red-700 font-semibold w-20 text-right">{eur(m.expenses)}</span>
                            </div>
                        </div>
                    ))}
                    {summary.monthly.length === 0 && (
                        <p className="text-sm text-zentra-muted py-3">Sin movimientos en el periodo.</p>
                    )}
                </div>
                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-zentra-border text-xs">
                    <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded bg-emerald-500" /> Ingresos pagados
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded bg-red-400" /> Gastos
                    </span>
                </div>
            </div>

            {/* Top categories */}
            {summary.by_category.length > 0 && (
                <div className="zentra-card-static p-5">
                    <h3 className="font-display font-semibold text-zentra-ink mb-3">Gastos por categoría</h3>
                    <div className="space-y-2">
                        {summary.by_category.map((c) => (
                            <div key={c.category} className="flex items-center justify-between text-sm">
                                <span className="text-zentra-ink">{EXPENSE_CATEGORY_LABELS[c.category] || c.category}</span>
                                <span className="font-display font-semibold text-zentra-ink">{eur(c.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function TaxBlock({ label, value, sub, highlight, testId }) {
    const tone = highlight === "warn"
        ? "text-amber-700"
        : highlight === "ok"
            ? "text-emerald-700"
            : "text-zentra-ink";
    return (
        <div className="px-5 py-4" data-testid={testId}>
            <p className="text-[11px] uppercase tracking-widest text-zentra-muted">{label}</p>
            <p className={`font-display text-2xl font-bold mt-1 ${tone}`}>{value}</p>
            {sub && <p className="text-[11px] text-zentra-muted mt-1">{sub}</p>}
        </div>
    );
}

function Statlet({ label, value, hint, highlight }) {
    return (
        <div className={`rounded-md p-3 border ${highlight ? "bg-zentra-accent/10 border-zentra-accent" : "bg-zentra-bg/40 border-zentra-border"}`}>
            <p className="text-[10px] uppercase tracking-widest text-zentra-muted">{label}</p>
            <p className={`font-display font-bold text-lg ${highlight ? "text-zentra-primary" : "text-zentra-ink"}`}>{value}</p>
            {hint && <p className="text-[10px] text-zentra-muted mt-0.5">{hint}</p>}
        </div>
    );
}

function InvoicesView({ invoices, clientMap, onUpdate, onDelete }) {
    if (invoices.length === 0) {
        return (
            <div className="zentra-card-static p-10 text-center">
                <FileText className="mx-auto text-zentra-muted mb-3" />
                <p className="font-display font-semibold">Sin facturas</p>
                <p className="text-sm text-zentra-muted">Crea la primera con "Nueva factura".</p>
            </div>
        );
    }
    return (
        <div className="zentra-card-static overflow-x-auto">
            <table className="zentra-table min-w-[1000px]" data-testid="invoices-table">
                <thead>
                    <tr>
                        <th>Número</th>
                        <th>Fecha</th>
                        <th>Cliente</th>
                        <th>Concepto</th>
                        <th className="text-right">Base</th>
                        <th className="text-right">IVA</th>
                        <th className="text-right">IRPF</th>
                        <th className="text-right">Total</th>
                        <th>Estado</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {invoices.map((inv) => (
                        <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                            <td className="font-mono-zentra text-xs">{inv.number}</td>
                            <td className="font-mono-zentra text-xs">{inv.issue_date}</td>
                            <td className="text-sm">{clientMap[inv.client_id]?.name || <span className="text-zentra-muted">—</span>}</td>
                            <td className="text-sm">{inv.concept}</td>
                            <td className="text-right">{eur(inv.subtotal || inv.amount)}</td>
                            <td className="text-right text-xs">
                                <span className="font-display font-semibold text-zentra-ink">{eur(inv.tax_amount)}</span>
                                <span className="text-zentra-muted ml-1">({inv.tax_rate}%)</span>
                            </td>
                            <td className="text-right text-xs">
                                {(inv.irpf_rate || 0) > 0 ? (
                                    <>
                                        <span className="font-display font-semibold text-red-700">− {eur(inv.irpf_amount)}</span>
                                        <span className="text-zentra-muted ml-1">({inv.irpf_rate}%)</span>
                                    </>
                                ) : (
                                    <span className="text-zentra-muted">—</span>
                                )}
                            </td>
                            <td className="text-right font-display font-semibold">{eur(inv.total)}</td>
                            <td>
                                <select
                                    className="zentra-select w-auto py-1 text-xs"
                                    value={inv.state}
                                    onChange={(e) => onUpdate(inv.id, { state: e.target.value })}
                                    data-testid={`invoice-state-${inv.id}`}
                                >
                                    {Object.entries(INVOICE_STATE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>
                                            {v.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td className="text-right">
                                <button
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50"
                                    onClick={() => onDelete(inv.id)}
                                >
                                    <Trash2 size={13} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ExpensesView({ expenses, onDelete }) {
    if (expenses.length === 0) {
        return (
            <div className="zentra-card-static p-10 text-center">
                <Receipt className="mx-auto text-zentra-muted mb-3" />
                <p className="font-display font-semibold">Sin gastos</p>
                <p className="text-sm text-zentra-muted">Crea el primero con "Nuevo gasto".</p>
            </div>
        );
    }
    return (
        <div className="zentra-card-static overflow-x-auto">
            <table className="zentra-table min-w-[900px]" data-testid="expenses-table">
                <thead>
                    <tr>
                        <th>Fecha</th>
                        <th>Categoría</th>
                        <th>Proveedor</th>
                        <th>Concepto</th>
                        <th className="text-right">Base</th>
                        <th className="text-right">IVA</th>
                        <th className="text-right">Total</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {expenses.map((e) => (
                        <tr key={e.id} data-testid={`expense-row-${e.id}`}>
                            <td className="font-mono-zentra text-xs">{e.date}</td>
                            <td>
                                <span className="zentra-chip bg-zentra-bg text-zentra-ink border-zentra-border">
                                    {EXPENSE_CATEGORY_LABELS[e.category] || e.category}
                                </span>
                            </td>
                            <td className="text-sm">{e.vendor}</td>
                            <td className="text-sm">{e.concept}</td>
                            <td className="text-right">{eur(e.subtotal || e.amount)}</td>
                            <td className="text-right text-xs">
                                <span className="font-display font-semibold text-zentra-ink">{eur(e.tax_amount || 0)}</span>
                                <span className="text-zentra-muted ml-1">({e.tax_rate ?? 21}%)</span>
                            </td>
                            <td className="text-right font-display font-semibold">{eur(e.total || e.amount)}</td>
                            <td className="text-right">
                                <button
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50"
                                    onClick={() => onDelete(e.id)}
                                >
                                    <Trash2 size={13} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function InvoiceModal({ open, onClose, clients, onSaved }) {
    const todayStr = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        number: "",
        issue_date: todayStr,
        due_date: "",
        client_id: "",
        amount: 0,
        tax_rate: 21,
        irpf_rate: 0,
        state: "draft",
        concept: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    useEffect(() => {
        if (open) {
            setForm({
                number: `${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`,
                issue_date: todayStr,
                due_date: "",
                client_id: "",
                amount: 0,
                tax_rate: 21,
                irpf_rate: 0,
                state: "draft",
                concept: "",
                notes: "",
            });
            setError("");
        }
    }, [open]); // eslint-disable-line

    const base = Number(form.amount) || 0;
    const iva = base * (Number(form.tax_rate) || 0) / 100;
    const irpf = base * (Number(form.irpf_rate) || 0) / 100;
    const total = base + iva - irpf;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.number || !form.amount) {
            setError("Número y base imponible son obligatorios");
            return;
        }
        setSaving(true);
        try {
            await api.post("/invoices", {
                ...form,
                amount: Number(form.amount),
                tax_rate: Number(form.tax_rate),
                irpf_rate: Number(form.irpf_rate),
                due_date: form.due_date || null,
                client_id: form.client_id || null,
            });
            onSaved?.();
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Nueva factura"
            wide
            testId="invoice-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button type="submit" form="inv-form" className="zentra-btn-primary" disabled={saving} data-testid="save-invoice">
                        {saving ? "Guardando…" : "Crear factura"}
                    </button>
                </>
            }
        >
            <form id="inv-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div>
                    <label className="zentra-label">Número</label>
                    <input
                        className="zentra-input font-mono-zentra"
                        required
                        value={form.number}
                        onChange={(e) => setForm({ ...form, number: e.target.value })}
                        data-testid="invoice-number"
                    />
                </div>
                <div>
                    <label className="zentra-label">Estado</label>
                    <select
                        className="zentra-select"
                        value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                    >
                        {Object.entries(INVOICE_STATE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>
                                {v.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="zentra-label">Fecha emisión</label>
                    <input
                        type="date"
                        className="zentra-input"
                        value={form.issue_date}
                        onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                    />
                </div>
                <div>
                    <label className="zentra-label">Vencimiento (opcional)</label>
                    <input
                        type="date"
                        className="zentra-input"
                        value={form.due_date}
                        onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    />
                </div>
                <div className="col-span-2">
                    <label className="zentra-label">Cliente</label>
                    <select
                        className="zentra-select"
                        value={form.client_id}
                        onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    >
                        <option value="">Sin cliente</option>
                        {clients.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-span-2">
                    <label className="zentra-label">Concepto</label>
                    <input
                        className="zentra-input"
                        value={form.concept}
                        onChange={(e) => setForm({ ...form, concept: e.target.value })}
                        placeholder="Servicio de limpieza mayo 2026"
                        data-testid="invoice-concept"
                    />
                </div>
                <div>
                    <label className="zentra-label">Base imponible (€)</label>
                    <input
                        type="number"
                        step="0.01"
                        className="zentra-input"
                        required
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        data-testid="invoice-amount"
                    />
                </div>
                <div>
                    <label className="zentra-label">IVA (%)</label>
                    <input
                        type="number"
                        step="0.5"
                        className="zentra-input"
                        value={form.tax_rate}
                        onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                    />
                    <p className="text-[10px] text-zentra-muted mt-1">General 21% · Reducido 10% · Súper reducido 4%</p>
                </div>
                <div>
                    <label className="zentra-label">IRPF retención (%)</label>
                    <input
                        type="number"
                        step="0.5"
                        className="zentra-input"
                        value={form.irpf_rate}
                        onChange={(e) => setForm({ ...form, irpf_rate: e.target.value })}
                        data-testid="invoice-irpf"
                    />
                    <p className="text-[10px] text-zentra-muted mt-1">Habitual: 0 (sociedades) · 7% (nuevos profesionales) · 15% (profesionales)</p>
                </div>
                <div className="col-span-2 rounded-md bg-zentra-bg/60 border border-zentra-border p-4 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-zentra-muted">Base imponible</span>
                        <span className="font-mono-zentra">{eur(base)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-zentra-muted">+ IVA ({form.tax_rate}%)</span>
                        <span className="font-mono-zentra">{eur(iva)}</span>
                    </div>
                    {(Number(form.irpf_rate) || 0) > 0 && (
                        <div className="flex items-center justify-between text-red-700">
                            <span>− IRPF ({form.irpf_rate}%)</span>
                            <span className="font-mono-zentra">− {eur(irpf)}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-zentra-border">
                        <span className="font-display font-semibold uppercase text-xs tracking-widest">Total a cobrar</span>
                        <span className="font-display text-2xl font-bold text-zentra-ink" data-testid="invoice-total-preview">
                            {eur(total)}
                        </span>
                    </div>
                </div>
                {error && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
            </form>
        </Modal>
    );
}

function ExpenseModal({ open, onClose, onSaved }) {
    const todayStr = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        date: todayStr,
        category: "other",
        amount: 0,
        tax_rate: 21,
        vendor: "",
        concept: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    useEffect(() => {
        if (open) {
            setForm({ date: todayStr, category: "other", amount: 0, tax_rate: 21, vendor: "", concept: "", notes: "" });
            setError("");
        }
    }, [open]); // eslint-disable-line

    const base = Number(form.amount) || 0;
    const iva = base * (Number(form.tax_rate) || 0) / 100;
    const total = base + iva;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.amount) {
            setError("Importe obligatorio");
            return;
        }
        setSaving(true);
        try {
            await api.post("/expenses", { ...form, amount: Number(form.amount), tax_rate: Number(form.tax_rate) });
            onSaved?.();
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Nuevo gasto"
            wide
            testId="expense-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button type="submit" form="exp-form" className="zentra-btn-primary" disabled={saving} data-testid="save-expense">
                        {saving ? "Guardando…" : "Crear gasto"}
                    </button>
                </>
            }
        >
            <form id="exp-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div>
                    <label className="zentra-label">Fecha</label>
                    <input
                        type="date"
                        className="zentra-input"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                </div>
                <div>
                    <label className="zentra-label">Categoría</label>
                    <select
                        className="zentra-select"
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        data-testid="expense-category"
                    >
                        {Object.entries(EXPENSE_CATEGORY_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>
                                {v}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="zentra-label">Proveedor</label>
                    <input
                        className="zentra-input"
                        value={form.vendor}
                        onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                    />
                </div>
                <div>
                    <label className="zentra-label">Importe base (€)</label>
                    <input
                        type="number"
                        step="0.01"
                        required
                        className="zentra-input"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        data-testid="expense-amount"
                    />
                </div>
                <div>
                    <label className="zentra-label">IVA soportado (%)</label>
                    <input
                        type="number"
                        step="0.5"
                        className="zentra-input"
                        value={form.tax_rate}
                        onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                        data-testid="expense-tax"
                    />
                    <p className="text-[10px] text-zentra-muted mt-1">El IVA de los gastos se deduce del IVA a pagar.</p>
                </div>
                <div className="col-span-2 rounded-md bg-zentra-bg/60 border border-zentra-border p-3 grid grid-cols-3 gap-3 text-sm">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zentra-muted">Base</p>
                        <p className="font-mono-zentra">{eur(base)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zentra-muted">IVA</p>
                        <p className="font-mono-zentra">{eur(iva)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zentra-muted">Total</p>
                        <p className="font-display font-bold text-lg">{eur(total)}</p>
                    </div>
                </div>
                <div className="col-span-2">
                    <label className="zentra-label">Concepto</label>
                    <input
                        className="zentra-input"
                        value={form.concept}
                        onChange={(e) => setForm({ ...form, concept: e.target.value })}
                        placeholder="Compra productos limpieza"
                        data-testid="expense-concept"
                    />
                </div>
                {error && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
            </form>
        </Modal>
    );
}
